import { decodeTime, ulid } from '@w/utils'
import type {
  LogData,
  SpanData,
  SpanEventData,
  CreateEventData,
  TraceData,
  TelParent,
  ResourceUserInfo,
} from './tel-types.js'
import { prepareForLog } from './prepare-for-log/prepare-for-log.js'
import { _makeSpan } from './span.js'
import type { TelErrorData } from './tel-error-data.js'
import type { TelMetricData } from './tel-metrics-types.js'
import { isTest, safe, hashString } from '@w/utils'
import { isClient } from './utils.js'

// FIFO Cleanup: When limits are exceeded, oldest items are removed using shift() to maintain recent data.
// Automatic Cleanup: When trace / request ends, it schedules cleanup of its resources. On client we preserve 3 last traces.
// errored spans / logs should be exported immediately without tail based sampling
// if trace / request is successful allow tail based sampling to export only shallow root span

// these options should be adjusted based on the amount of concurrent users on server and browser memory
const DEFAULT_LOGS_STORAGE_LIMIT = isClient ? 100 : 200
const DEFAULT_SPANS_STORAGE_LIMIT = isClient ? 500 : 1000
const DEFAULT_SPAN_EVENTS_STORAGE_LIMIT = 200 // client only
const STRUCTURAL_DEDUPE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const STRUCTURAL_DEDUPE_MAX_SIZE = isClient ? 100 : 0
const DEFAULT_METRICS_STORAGE_LIMIT = 1000
const DEFAULT_EVENTS_BUFFER_LIMIT = isClient ? 20 : 30
const DEFAULT_METRICS_BUFFER_LIMIT = 100

// Exported because `_telStorage`'s inferred type names it: declaration emit cannot
// write a .d.ts for a public value whose type lives in an unexported local.
export interface TelUserDataStore {
  get(): ResourceUserInfo
  set(data: ResourceUserInfo): void
  update(data: Partial<ResourceUserInfo>): void
  subscribe(callback: (data: ResourceUserInfo) => void): () => void
  clear(): void
}

export interface TelStorageConfig {
  logsStorageLimit?: number
  spansStorageLimit?: number
  spanEventsStorageLimit?: number
  metricsStorageLimit?: number
  eventsBufferLimit?: number
  metricsBufferLimit?: number
  errorFilterStrings?: string[]
  errorMapper?: (error: unknown) => unknown
  /**
   * Whether spans record WHAT THEY PRODUCED. Defaults to `false`, and that
   * default is the ruling (2026-08-24): a span ends with whatever its callback
   * returned and @w/be-otel-export ships that as its own log event, so a db
   * span's rows land in the log pipeline in full — a production be-va log
   * carried a customer call row, phone number and transcript included.
   *
   * FAIL-CLOSED, so a process that configures nothing — a new service, a cron
   * nobody remembered was its own tel root, a frontend — leaks nothing. What
   * survives the gate is what diagnoses without disclosing: db spans keep
   * `{ rowCount }`, every span keeps its `outputSize`, and INPUT is untouched
   * (db params are masked upstream).
   *
   * `true` is therefore the deliberate OPT-IN, for debugging worth the
   * exposure. Service-wide:
   *
   *   makeRootTel({ storageConfig: { captureSpanOutput: true } })
   *
   * One span at a time: `StartSpanOpts.captureOutput`, which wins over this in
   * either direction and does NOT inherit to child spans.
   *
   * This is the ONLY place the default is declared. `_makeSpan` requires the
   * getter rather than defaulting behind it, so there is no second opinion to
   * drift open.
   */
  captureSpanOutput?: boolean
}

export function makeTelStorage(config: TelStorageConfig = {}) {
  let {
    logsStorageLimit = DEFAULT_LOGS_STORAGE_LIMIT,
    spansStorageLimit = DEFAULT_SPANS_STORAGE_LIMIT,
    spanEventsStorageLimit = DEFAULT_SPAN_EVENTS_STORAGE_LIMIT,
    metricsStorageLimit = DEFAULT_METRICS_STORAGE_LIMIT,
    eventsBufferLimit = DEFAULT_EVENTS_BUFFER_LIMIT,
    metricsBufferLimit = DEFAULT_METRICS_BUFFER_LIMIT,
    errorFilterStrings = [],
    errorMapper,
    // The fail-closed default, declared once for the whole package.
    captureSpanOutput = false,
  } = config

  let handlersRegistered = false
  const scheduledCleanups = new Set<ReturnType<typeof setTimeout>>()
  const finishedTraces: { traceId: string; endTime: number }[] = [] // client only
  const flushCallbacks = new Set<() => Promise<void>>() // Store flush callbacks
  const handlers: TelStorageEventHandlers = {
    logs: {},
    spans: {
      events: {},
    },
    warnings: {},
    metrics: {},
  }

  // User data store
  let currentUserData: ResourceUserInfo = {}
  const userDataSubscribers = new Set<(data: ResourceUserInfo) => void>()

  const userDataStore: TelUserDataStore = {
    get() {
      return { ...currentUserData } // Return copy to prevent mutations
    },

    set(data: ResourceUserInfo) {
      currentUserData = { ...data }
      userDataSubscribers.forEach((callback) => callback(currentUserData))
    },

    update(data: Partial<ResourceUserInfo>) {
      currentUserData = { ...currentUserData, ...data }
      userDataSubscribers.forEach((callback) => callback(currentUserData))
    },

    subscribe(callback: (data: ResourceUserInfo) => void) {
      userDataSubscribers.add(callback)
      return () => userDataSubscribers.delete(callback)
    },

    clear() {
      currentUserData = {}
      userDataSubscribers.forEach((callback) => callback(currentUserData))
    },
  }

  const storage = {
    logs: [] as LogData[],
    spans: [] as SpanData[],
    spanEvents: [] as SpanEventData[],
    metrics: [] as TelMetricData[],
  }

  // Track seen errors across their cause-chain to avoid duplicate reporting
  const seenErrors = new WeakSet<object>()
  // WeakSet ensures no memory leaks: entries are garbage-collected once errors are unreachable

  // Structural dedupe cache: map signature -> last seen timestamp
  const structuralErrorCache = new Map<string, number>()

  function pruneStructuralCache(now: number = Date.now()) {
    if (structuralErrorCache.size === 0) return
    // Remove expired
    for (const [sig, ts] of structuralErrorCache) {
      if (now - ts > STRUCTURAL_DEDUPE_TTL_MS) structuralErrorCache.delete(sig)
    }
    // Enforce max size by evicting oldest
    if (structuralErrorCache.size > STRUCTURAL_DEDUPE_MAX_SIZE) {
      const entries = Array.from(structuralErrorCache.entries()).sort((a, b) => a[1] - b[1])
      const excess = structuralErrorCache.size - STRUCTURAL_DEDUPE_MAX_SIZE
      for (let i = 0; i < excess; i++) structuralErrorCache.delete(entries[i][0])
    }
  }

  // Build a stable signature for an error-like object
  function buildErrorSignature(obj: unknown): string {
    if (obj && typeof obj === 'object') {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const anyObj = obj as any
      const name = String(anyObj.name || anyObj.constructor?.name || 'Error')
      const message = String(anyObj.message ?? '')
      const stack = String(anyObj.stack ?? '')
      return hashString(`${name}|${message}|${stack}`)
    }
    try {
      return hashString(`${typeof obj}|${String(obj)}`)
    } catch {
      return 'unknown'
    }
  }

  const eventsBuffer = {
    logs: {
      create: [] as LogData[],
    },
    span: {
      events: {
        create: [] as SpanEventData[],
      },
      create: [] as SpanData[],
      update: [] as Partial<SpanData>[],
      end: [] as Partial<SpanData>[],
    },
    trace: {
      create: [] as SpanData[],
      end: [] as (() => TraceData)[],
    },
    requests: {
      create: [] as SpanData[],
      end: [] as (() => TraceData)[],
    },
    errors: {
      create: [] as {
        data: TelErrorData
        originalError: unknown
        userInfo: ResourceUserInfo
        getTrace: () => TraceData | null
      }[],
    },
    warnings: {
      create: [] as {
        data: TelErrorData
        originalError: unknown
        userInfo: ResourceUserInfo
      }[],
    },
    metrics: {
      create: [] as TelMetricData[],
    },
  }

  const storageObj = {
    userDataStore,
    logs: {
      create(data: LogData) {
        data.data = prepareForLog(data.data, { removeMark: true })
        storage.logs.push(data)
        if (storage.logs.length > logsStorageLimit) storage.logs.shift()
        if (handlersRegistered) handlers.logs?.onCreate?.(data)
        else if (eventsBuffer.logs.create.length < eventsBufferLimit)
          eventsBuffer.logs.create.push(data)
      },
    },
    metrics: {
      create(data: TelMetricData) {
        storage.metrics.push(data)
        if (storage.metrics.length > metricsStorageLimit) storage.metrics.shift()

        if (handlersRegistered) {
          handlers.metrics?.onCreate?.(data)
        } else if (eventsBuffer.metrics.create.length < metricsBufferLimit) {
          eventsBuffer.metrics.create.push(data)
        }
      },
    },
    spans: {
      events: {
        create(
          eventOrKey: CreateEventData | string,
          cb: ((old?: CreateEventData) => CreateEventData) | undefined,
          overrides: Partial<SpanEventData> = {},
        ) {
          let event: SpanEventData
          if (typeof eventOrKey === 'string') {
            const old = storage.spanEvents.find((e) => e.key === eventOrKey)
            if (!cb) throw new Error('impossible')
            const data = cb(old)
            event = { ...data, id: ulid(), ...overrides, key: eventOrKey }
          } else {
            event = { ...eventOrKey, id: ulid(), ...overrides }
          }

          event.data = prepareForLog(event.data, { removeMark: true })

          storage.spanEvents.push(event)
          if (storage.spanEvents.length > spanEventsStorageLimit) storage.spanEvents.shift()

          if (handlersRegistered) handlers.spans?.events?.onCreate?.(event)
          else if (eventsBuffer.span.events.create.length < eventsBufferLimit)
            eventsBuffer.span.events.create.push(event)
        },
      },
      /**
       * `captureOutput` rides in with the span fields and is split off HERE,
       * once. It is a directive, not span data — it steers this span's gate and
       * must never reach SpanData, where it would be exported. Every caller
       * used to destructure it itself and rebuild a wrapper object for it,
       * which is three chances to forget rather than none.
       */
      create(data: SpanData & { captureOutput?: boolean }, parent?: TelParent) {
        const { captureOutput, ...spanData } = data
        // Only apply user data for route spans
        const isRouteSpan = spanData.target === 'router'
        const freshUserData = isRouteSpan ? userDataStore.get() : {}

        const span = _makeSpan(
          {
            ...spanData,
            parentId: spanData.parentId || parent?.id,
            requestId: spanData.requestId || parent?.requestId,
            sessionId: spanData.sessionId || parent?.sessionId,
            traceId: spanData.traceId || parent?.traceId,
            // Override with fresh user data only for route spans and if not explicitly set
            ...(isRouteSpan && {
              userId: spanData.userId || freshUserData.userId,
              entityUserId: spanData.entityUserId || freshUserData.entityUserId,
              companyId: spanData.companyId || freshUserData.companyId,
              entityId: spanData.entityId || freshUserData.entityId,
            }),
          },
          {
            end: storageObj.spans.end,
            update: storageObj.spans.update,
            errors: storageObj.spans.errors,
            getErrorFilterStrings: storageObj.getErrorFilterStrings,
            getErrorMapper: storageObj.getErrorMapper,
            isSpanOutputCaptured: storageObj.isSpanOutputCaptured,
          },
          { captureOutput },
        )

        storage.spans.push(span.data)
        if (storage.spans.length > spansStorageLimit) storage.spans.shift()

        // Check if this is a root span (start of a new trace) - for traces, id and traceId are the same
        const isRootSpan = span.data.id === span.data.traceId
        if (isRootSpan) {
          if (handlersRegistered) {
            handlers.traces?.onCreate?.(span.data)
          } else if (eventsBuffer.trace.create.length < eventsBufferLimit) {
            eventsBuffer.trace.create.push(span.data)
          }
        }

        const isRequestSpan =
          span.data.target === 'trpc-handler' || span.data.target === 'server-handler'
        if (isRequestSpan) {
          if (handlersRegistered) {
            handlers.requests?.onCreate?.(span.data)
          } else if (eventsBuffer.requests.create.length < eventsBufferLimit) {
            eventsBuffer.requests.create.push(span.data)
          }
        }

        if (handlersRegistered)
          handlers.spans?.onCreate?.(span.data) // it can't be just data because it is not handled by prepareForLog. in other methods it's not applicable because they should be called from Span instances with proper data handling.
        else if (eventsBuffer.span.create.length < eventsBufferLimit)
          eventsBuffer.span.create.push(span.data)

        return span
      },
      update(data: Partial<SpanData> & { id: string }) {
        const span = storage.spans.find((span) => span.id === data.id)
        if (!span) {
          if (isTest) return
          return console.error('span not found')
        }
        Object.assign(span, data)

        if (handlersRegistered) handlers.spans?.onUpdate?.(span, data)
        else if (eventsBuffer.span.update.length < eventsBufferLimit)
          eventsBuffer.span.update.push(data)
      },
      end(data: Partial<SpanData> & { id: string }) {
        const span = storage.spans.find((span) => span.id === data.id)
        if (!span) {
          if (isTest) return
          return console.error('span not found')
        }
        // merge attributes
        if (data.attributes) {
          Object.assign(span.attributes || {}, data.attributes)
          // biome-ignore lint/performance/noDelete: <explanation>
          delete data.attributes
        }

        Object.assign(span, data)

        if (handlersRegistered) {
          const getTrace = () =>
            reconstructTrace(span.id, storage.spans, storage.spanEvents, storage.logs)
          handlers.spans?.onEnd?.(span, data, getTrace)
          handlers.spans?.onUpdate?.(span, data)

          // Check if trace is complete (all spans finished)
          if (span.traceId && !storageObj.hasUnfinishedSpans(span.traceId)) {
            const getTraceForEnd = () => {
              const trace = reconstructTrace(
                span.id,
                storage.spans,
                storage.spanEvents,
                storage.logs,
              )
              if (!trace) throw new Error('Trace not found')
              return trace
            }
            handlers.traces?.onEnd?.(getTraceForEnd)

            // Schedule cleanup of trace resources
            storageObj.scheduleTraceCleanup(span.traceId)
          }

          const isRequestSpan = span.target === 'trpc-handler' || span.target === 'server-handler'
          if (isRequestSpan) {
            const getTraceForEnd = () => {
              const trace = reconstructTrace(
                span.id,
                storage.spans,
                storage.spanEvents,
                storage.logs,
              )
              if (!trace) throw new Error('Trace not found')
              return trace
            }
            handlers.requests?.onEnd?.(getTraceForEnd)

            // Schedule cleanup of request resources (server only)
            storageObj.scheduleRequestCleanup(span.id)
          }
        } else if (eventsBuffer.span.end.length < eventsBufferLimit) {
          eventsBuffer.span.end.push(data)
          eventsBuffer.span.update.push(data)

          // Check if trace is complete and buffer the trace end event
          if (span.traceId && !storageObj.hasUnfinishedSpans(span.traceId)) {
            const getTraceForEnd = () => {
              const trace = reconstructTrace(
                span.id,
                storage.spans,
                storage.spanEvents,
                storage.logs,
              )
              if (!trace) throw new Error('Trace not found')
              return trace
            }
            if (eventsBuffer.trace.end.length < eventsBufferLimit) {
              eventsBuffer.trace.end.push(getTraceForEnd)
            }

            // Schedule cleanup of trace resources
            storageObj.scheduleTraceCleanup(span.traceId)
          }

          // Check if request is complete and buffer the request end event
          const isRequestSpan = span.target === 'trpc-handler' || span.target === 'server-handler'
          if (isRequestSpan) {
            const getTraceForEnd = () => {
              const trace = reconstructTrace(
                span.id,
                storage.spans,
                storage.spanEvents,
                storage.logs,
              )
              if (!trace) throw new Error('Trace not found')
              return trace
            }
            if (eventsBuffer.requests.end.length < eventsBufferLimit) {
              eventsBuffer.requests.end.push(getTraceForEnd)
            }

            // Schedule cleanup of request resources (server only)
            storageObj.scheduleRequestCleanup(span.id)
          }
        }
      },
      errors: {
        create(originalError: unknown, data: TelErrorData, userInfo: ResourceUserInfo = {}) {
          if (!originalError) return

          // Apply error mapper if configured
          const mappedError = errorMapper ? errorMapper(originalError) : originalError

          // Get fresh user data from store and merge with provided userInfo
          const freshUserData = userDataStore.get()
          const finalUserInfo = { ...freshUserData, ...userInfo }

          // Deduplicate by traversing the cause-chain (cycle-safe) and checking WeakSet
          const chain: object[] = []
          const visited = new WeakSet<object>()
          let current: unknown = mappedError
          let depth = 0
          const MAX_DEPTH = 20
          while (current && typeof current === 'object' && depth < MAX_DEPTH) {
            const currentObj = current as object
            if (visited.has(currentObj)) break
            visited.add(currentObj)
            chain.push(currentObj)
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            const next = 'cause' in (current as any) ? (current as any).cause : undefined
            if (!next || typeof next !== 'object') break
            current = next
            depth++
          }

          // If any object in the chain was already seen, skip creating a duplicate error
          if (chain.some((e) => seenErrors.has(e))) return

          // Structural dedupe (client-only): skip if same signature seen within TTL for any cause
          let chainSignatures: string[] = []
          let now = Date.now()
          if (isClient) {
            pruneStructuralCache()
            now = Date.now()
            chainSignatures = chain.map((e) => buildErrorSignature(e))
            const withinTtl = chainSignatures.some((sig) => {
              const ts = structuralErrorCache.get(sig)
              return ts !== undefined && now - ts <= STRUCTURAL_DEDUPE_TTL_MS
            })
            if (withinTtl) return
          }
          if (!data) return

          if (data.level === 'warning') {
            if (handlersRegistered) handlers.warnings?.onCreate?.(data, mappedError, finalUserInfo)
            else if (eventsBuffer.warnings.create.length < eventsBufferLimit)
              eventsBuffer.warnings.create.push({
                data,
                originalError: mappedError,
                userInfo: finalUserInfo,
              })
          } else {
            if (handlersRegistered) {
              const getTrace = () =>
                reconstructTrace(data.spanId || '', storage.spans, storage.spanEvents, storage.logs)
              handlers.errors?.onCreate?.(data, mappedError, finalUserInfo, getTrace)
            } else if (eventsBuffer.errors.create.length < eventsBufferLimit) {
              const getTrace = () =>
                reconstructTrace(data.spanId || '', storage.spans, storage.spanEvents, storage.logs)
              eventsBuffer.errors.create.push({
                data,
                originalError: mappedError,
                userInfo: finalUserInfo,
                getTrace,
              })
            }
          }

          // Mark the entire chain as seen to prevent future duplicates
          for (const e of chain) seenErrors.add(e)

          // Update structural dedupe timestamps for all signatures in the chain (client-only)
          if (isClient && chainSignatures.length > 0) {
            for (const sig of chainSignatures) structuralErrorCache.set(sig, now)
          }
        },
      },
    },

    hasUnfinishedSpans(traceId: string): boolean {
      if (!traceId) return false

      // Find spans belonging to this trace
      const traceSpans = storage.spans.filter((span) => span.traceId === traceId)

      if (traceSpans.length === 0) return false

      // Check if any spans are unfinished
      // A span is finished if it has a status and duration
      const unfinishedSpans = traceSpans.filter((span) => {
        const hasStatus =
          span.status &&
          (span.status === 'ok' || span.status === 'error' || span.status === 'warning')
        const hasDuration = typeof span.duration === 'number'
        return !hasStatus || !hasDuration
      })

      return unfinishedSpans.length > 0
    },

    cleanupTrace(traceId: string) {
      if (!traceId) return

      storage.spans = storage.spans.filter((span) => span.traceId !== traceId)
      storage.spanEvents = storage.spanEvents.filter((event) => event.traceId !== traceId)
      storage.logs = storage.logs.filter((log) => log.traceId !== traceId)

      // Remove from finished traces tracking (client only)
      if (isClient) {
        const index = finishedTraces.findIndex((trace) => trace.traceId === traceId)
        if (index !== -1) {
          finishedTraces.splice(index, 1)
        }
      }
    },

    cleanupRequest(requestId: string) {
      if (!requestId) return

      storage.spans = storage.spans.filter((span) => span.requestId !== requestId)
      storage.spanEvents = storage.spanEvents.filter((event) => event.requestId !== requestId)
      storage.logs = storage.logs.filter((log) => log.requestId !== requestId)
    },

    scheduleTraceCleanup(traceId: string) {
      if (!traceId) return

      if (isClient) {
        // On client: track finished traces and only keep 3 most recent
        finishedTraces.push({ traceId, endTime: Date.now() })

        // Remove oldest traces if we exceed the limit (FIFO)
        while (finishedTraces.length > 3) {
          const oldestTrace = finishedTraces.shift() // Remove the first (oldest) trace
          if (oldestTrace) {
            storageObj.cleanupTrace(oldestTrace.traceId)
          }
        }
      } else {
        // On server: clean immediately after trace ends
        const timeoutId = setTimeout(() => {
          scheduledCleanups.delete(timeoutId)
          storageObj.cleanupTrace(traceId)
        }, 0)
        scheduledCleanups.add(timeoutId)
      }
    },

    scheduleRequestCleanup(requestId: string) {
      if (!requestId) return

      // On server only: clean immediately after request ends
      if (!isClient) {
        const timeoutId = setTimeout(() => {
          scheduledCleanups.delete(timeoutId)
          storageObj.cleanupRequest(requestId)
        }, 0)
        scheduledCleanups.add(timeoutId)
      }
    },

    updateConfig(newConfig: TelStorageConfig) {
      const {
        logsStorageLimit: newLogsStorageLimit,
        spansStorageLimit: newSpansStorageLimit,
        spanEventsStorageLimit: newSpanEventsStorageLimit,
        metricsStorageLimit: newMetricsStorageLimit,
        eventsBufferLimit: newEventsBufferLimit,
        metricsBufferLimit: newMetricsBufferLimit,
        errorFilterStrings: newErrorFilterStrings,
        errorMapper: newErrorMapper,
        captureSpanOutput: newCaptureSpanOutput,
      } = newConfig

      if (newLogsStorageLimit !== undefined) {
        logsStorageLimit = newLogsStorageLimit
      }
      if (newSpansStorageLimit !== undefined) {
        spansStorageLimit = newSpansStorageLimit
      }
      if (newSpanEventsStorageLimit !== undefined) {
        spanEventsStorageLimit = newSpanEventsStorageLimit
      }
      if (newMetricsStorageLimit !== undefined) {
        metricsStorageLimit = newMetricsStorageLimit
      }
      if (newEventsBufferLimit !== undefined) {
        eventsBufferLimit = newEventsBufferLimit
      }
      if (newMetricsBufferLimit !== undefined) {
        metricsBufferLimit = newMetricsBufferLimit
      }
      if (newErrorFilterStrings !== undefined) {
        errorFilterStrings = newErrorFilterStrings
      }
      if (newErrorMapper !== undefined) {
        errorMapper = newErrorMapper
      }
      if (newCaptureSpanOutput !== undefined) {
        captureSpanOutput = newCaptureSpanOutput
      }
    },

    setHandlers(...inputHandlers: TelStorageEventHandlers[]) {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      function mergeHandler<T extends (...args: any[]) => any>(
        existing: T | undefined,
        newHandler: T | undefined,
      ): T | undefined {
        if (!newHandler) return existing
        if (!existing) return newHandler

        return ((...args: Parameters<T>) => {
          const existingResult = existing(...args)
          const newResult = newHandler(...args)

          // Check if either result is a Promise and handle accordingly
          if (existingResult instanceof Promise || newResult instanceof Promise) {
            return Promise.all([
              existingResult instanceof Promise ? existingResult : Promise.resolve(existingResult),
              newResult instanceof Promise ? newResult : Promise.resolve(newResult),
            ])
          }

          return newResult
        }) as T
      }

      const eventHandlers = inputHandlers.reduce(
        (acc: Required<TelStorageEventHandlers>, handler) => {
          acc.logs.onCreate = mergeHandler(acc.logs.onCreate, handler.logs?.onCreate)
          safe(acc.spans.events).onCreate = mergeHandler(
            safe(acc.spans.events).onCreate,
            handler.spans?.events?.onCreate,
          )
          acc.spans.onCreate = mergeHandler(acc.spans.onCreate, handler.spans?.onCreate)
          acc.spans.onUpdate = mergeHandler(acc.spans.onUpdate, handler.spans?.onUpdate)
          acc.spans.onEnd = mergeHandler(acc.spans.onEnd, handler.spans?.onEnd)
          acc.traces.onCreate = mergeHandler(acc.traces.onCreate, handler.traces?.onCreate)
          acc.traces.onEnd = mergeHandler(acc.traces.onEnd, handler.traces?.onEnd)
          acc.requests.onCreate = mergeHandler(acc.requests.onCreate, handler.requests?.onCreate)
          acc.requests.onEnd = mergeHandler(acc.requests.onEnd, handler.requests?.onEnd)
          acc.errors.onCreate = mergeHandler(acc.errors.onCreate, handler.errors?.onCreate)
          acc.warnings.onCreate = mergeHandler(acc.warnings.onCreate, handler.warnings?.onCreate)
          acc.metrics.onCreate = mergeHandler(acc.metrics.onCreate, handler.metrics?.onCreate)
          return acc
        },
        {
          logs: {},
          spans: { events: {} },
          traces: {},
          requests: {},
          errors: {},
          warnings: {},
          metrics: {},
        } as Required<TelStorageEventHandlers>,
      )

      handlersRegistered = true
      Object.assign(handlers, eventHandlers)

      if (eventsBuffer.logs.create.length > 0) {
        for (const data of eventsBuffer.logs.create) eventHandlers.logs?.onCreate?.(data)
        eventsBuffer.logs.create = []
      }

      if (eventsBuffer.span.events.create.length > 0) {
        for (const data of eventsBuffer.span.events.create)
          eventHandlers.spans?.events?.onCreate?.(data)
        eventsBuffer.span.events.create = []
      }

      if (eventsBuffer.span.create.length > 0) {
        for (const data of eventsBuffer.span.create) eventHandlers.spans?.onCreate?.(data)
        eventsBuffer.span.create = []
      }
      if (eventsBuffer.span.update.length > 0) {
        for (const data of eventsBuffer.span.update) {
          const span = storage.spans.find((span) => span.id === data.id)
          if (!span) {
            if (isTest) return
            return console.error('span not found')
          }
          eventHandlers.spans?.onUpdate?.(span, data)
        }
        eventsBuffer.span.update = []
      }
      if (eventsBuffer.span.end.length > 0) {
        for (const data of eventsBuffer.span.end) {
          const span = storage.spans.find((span) => span.id === data.id)
          if (!span) {
            if (isTest) return
            return console.error('span not found')
          }
          const getTrace = () =>
            reconstructTrace(span.id, storage.spans, storage.spanEvents, storage.logs)
          eventHandlers.spans?.onEnd?.(span, data, getTrace)
        }
        eventsBuffer.span.end = []
      }

      if (eventsBuffer.trace.create.length > 0) {
        for (const data of eventsBuffer.trace.create) eventHandlers.traces?.onCreate?.(data)
        eventsBuffer.trace.create = []
      }

      if (eventsBuffer.trace.end.length > 0) {
        for (const getTrace of eventsBuffer.trace.end) eventHandlers.traces?.onEnd?.(getTrace)
        eventsBuffer.trace.end = []
      }

      if (eventsBuffer.requests.create.length > 0) {
        for (const data of eventsBuffer.requests.create) eventHandlers.requests?.onCreate?.(data)
        eventsBuffer.requests.create = []
      }

      if (eventsBuffer.requests.end.length > 0) {
        for (const getTrace of eventsBuffer.requests.end) eventHandlers.requests?.onEnd?.(getTrace)
        eventsBuffer.requests.end = []
      }

      if (eventsBuffer.errors.create.length > 0) {
        for (const { data, originalError, userInfo, getTrace } of eventsBuffer.errors.create) {
          eventHandlers.errors?.onCreate?.(data, originalError, userInfo, getTrace)
        }
        eventsBuffer.errors.create = []
      }

      if (eventsBuffer.warnings.create.length > 0) {
        for (const { data, originalError, userInfo } of eventsBuffer.warnings.create)
          eventHandlers.warnings?.onCreate?.(data, originalError, userInfo)
        eventsBuffer.warnings.create = []
      }

      if (eventsBuffer.metrics.create.length > 0) {
        for (const data of eventsBuffer.metrics.create) {
          eventHandlers.metrics?.onCreate?.(data)
        }
        eventsBuffer.metrics.create = []
      }
    },
    clear() {
      // Clear scheduled cleanup timers
      for (const timeoutId of scheduledCleanups) {
        clearTimeout(timeoutId)
      }
      scheduledCleanups.clear()

      eventsBuffer.span.create = []
      eventsBuffer.span.update = []
      eventsBuffer.span.end = []

      eventsBuffer.span.events.create = []

      eventsBuffer.logs.create = []

      storage.logs = []
      storage.spans = []
      storage.spanEvents = []
      storage.metrics = []

      eventsBuffer.errors.create = []
      eventsBuffer.warnings.create = []
      eventsBuffer.trace.create = []
      eventsBuffer.trace.end = []
      eventsBuffer.requests.create = []
      eventsBuffer.requests.end = []
      eventsBuffer.metrics.create = []

      // Clear finished traces tracking (client only)
      if (isClient) {
        finishedTraces.length = 0
      }

      structuralErrorCache.clear()
    },

    _listTraces(): SpanData[] {
      // Get all root spans (spans where id === traceId)
      return storage.spans
        .filter((span) => {
          return span.id === span.traceId
        })
        .sort((a, b) => decodeTime(b.id) - decodeTime(a.id))
    },

    _getTrace(traceId: string): TraceData | null {
      if (!traceId) return null

      // Find the root span for this trace
      const rootSpan = storage.spans.find(
        (span) => span.traceId === traceId && span.id === span.traceId,
      )
      if (!rootSpan) return null

      return reconstructTrace(rootSpan.id, storage.spans, storage.spanEvents, storage.logs)
    },

    getErrorFilterStrings() {
      return errorFilterStrings
    },

    getErrorMapper() {
      return errorMapper
    },

    isSpanOutputCaptured() {
      return captureSpanOutput
    },

    registerFlush(callback: () => Promise<void>) {
      flushCallbacks.add(callback)
    },

    /**
     * Runs every registered flush callback and waits for all of them to
     * settle. Sinks are independent (span batcher, log batcher, Sentry, ...),
     * so one failing must neither stop the others nor hide which one failed.
     * Never rejects: shutdown paths call this and must always reach exit.
     */
    async flush() {
      // async wrapper so a callback that throws synchronously becomes a rejection
      const results = await Promise.allSettled(
        Array.from(flushCallbacks).map(async (callback) => callback()),
      )
      for (const result of results) {
        if (result.status === 'rejected') {
          console.error('Failed to flush telemetry:', result.reason)
        }
      }
    },
  }

  return storageObj
}

export interface TelStorageEventHandlers {
  logs?: {
    onCreate?: (data: LogData) => void
  }
  spans?: Partial<{
    events: {
      onCreate?: (data: SpanEventData) => void
    }
    onCreate: (data: SpanData) => void
    onUpdate: (span: SpanData, update: Partial<SpanData>) => void
    onEnd: (span: SpanData, update: Partial<SpanData>, getTrace: () => TraceData | null) => void
  }>
  traces?: {
    onCreate?: (span: SpanData) => void
    onEnd?: (getTrace: () => TraceData) => void
  }
  requests?: {
    onCreate?: (span: SpanData) => void
    onEnd?: (getTrace: () => TraceData) => void
  }
  errors?: {
    onCreate?: (
      data: TelErrorData,
      originalError: unknown,
      userInfo: ResourceUserInfo,
      getTrace: () => TraceData | null,
    ) => Promise<void> | void
  }
  warnings?: {
    onCreate?: (
      data: TelErrorData,
      originalError: unknown,
      userInfo: ResourceUserInfo,
    ) => Promise<void> | void
  }
  metrics?: {
    onCreate?: (data: TelMetricData) => void
  }
}

// Helper function to reconstruct trace data from stored spans
function reconstructTrace(
  targetSpanId: string,
  spans: SpanData[],
  spanEvents: SpanEventData[],
  logs: LogData[],
): TraceData | null {
  if (!targetSpanId) return null

  const targetSpan = spans.find((span) => span.id === targetSpanId)
  if (!targetSpan) return null

  // Find the root span of this trace by following the parent chain
  let rootSpan = targetSpan
  const visited = new Set<string>() // Prevent infinite loops in case of circular references

  while (rootSpan.parentId && !visited.has(rootSpan.id)) {
    visited.add(rootSpan.id)
    const parent = spans.find((span) => span.id === rootSpan.parentId)
    if (!parent) break
    rootSpan = parent
  }

  // Create lookup maps for better performance
  const spansByParent = new Map<string, SpanData[]>()
  const eventsBySpan = new Map<string, SpanEventData[]>()
  const logsBySpan = new Map<string, LogData[]>()

  // Group spans by parent for efficient tree building
  for (const span of spans) {
    if (span.parentId) {
      if (!spansByParent.has(span.parentId)) {
        spansByParent.set(span.parentId, [])
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      spansByParent.get(span.parentId)!.push(span)
    }
  }

  // Group events and logs by span/trace
  for (const event of spanEvents) {
    const key = event.parentId || event.traceId || ''
    if (key) {
      if (!eventsBySpan.has(key)) {
        eventsBySpan.set(key, [])
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      eventsBySpan.get(key)!.push(event)
    }
  }

  for (const log of logs) {
    const key = log.parentId || log.traceId || ''
    if (key) {
      if (!logsBySpan.has(key)) {
        logsBySpan.set(key, [])
      }
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      logsBySpan.get(key)!.push(log)
    }
  }

  // Build trace tree recursively
  function buildTraceNode(span: SpanData): TraceData {
    const children = (spansByParent.get(span.id) || []).map(buildTraceNode)

    // Get events for this span
    // For root spans (id === traceId), also include trace-level events
    const events = [
      ...(eventsBySpan.get(span.id) || []),
      ...(span.id === span.traceId ? eventsBySpan.get(span.traceId) || [] : []),
    ].filter(
      (event, index, array) =>
        // Remove duplicates based on event id
        array.findIndex((e) => e.id === event.id) === index,
    )

    // Get logs for this span
    // For root spans (id === traceId), also include trace-level logs
    const spanLogs = [
      ...(logsBySpan.get(span.id) || []),
      ...(span.id === span.traceId ? logsBySpan.get(span.traceId) || [] : []),
    ].filter(
      (log, index, array) =>
        // Remove duplicates based on log id
        array.findIndex((l) => l.id === log.id) === index,
    )

    return {
      ...span,
      events,
      logs: spanLogs,
      children,
    }
  }

  return buildTraceNode(rootSpan)
}
